import { useQuery } from '@tanstack/react-query';

import { TokenUsageWithBreakdown } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/h2ogpte/v1/h2ogpte_pb';
import { useTelemetryService } from '../../../telemetry/hooks';
import { TokenUsageDataParams } from '../types';

/**
 * Custom hook to fetch H2OGPTe token usage data from the telemetry service using React Query
 *
 * Fetches token usage data based on the provided parameters and manages
 * loading, error, and caching states automatically.
 *
 * @param params Parameters for fetching token usage data (dates, granularity, grouping, filters)
 * @returns Object containing token usage data, loading state, error, and refetch function
 */
export function useTokenUsageData(params: TokenUsageDataParams) {
  const { startTime, endTime, granularity, groupBy, selectedModels, selectedWorkspaces, selectedUsers } = params;
  const TelemetryService = useTelemetryService();

  const query = useQuery<TokenUsageWithBreakdown[], Error>(
    // Query key - refetch when any of these change
    [
      'tokenUsageData',
      {
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        granularity,
        groupBy,
        modelsKey: selectedModels.length > 0 ? [...selectedModels].sort().join(',') : 'none',
        workspacesKey: selectedWorkspaces.length > 0 ? [...selectedWorkspaces].sort().join(',') : 'none',
        usersKey: selectedUsers.length > 0 ? [...selectedUsers].sort().join(',') : 'none',
      },
    ],
    // Query function
    async () => {
      const startTimeISO = startTime.toISOString();
      const endTimeISO = endTime.toISOString();

      // Build combined filter string from all filter types
      let filterString: string | undefined = undefined;
      const filterParts: string[] = [];

      // Model filter
      if (selectedModels.length > 0) {
        const modelFilter = selectedModels.map((model) => `model = '${model}'`).join(' OR ');
        filterParts.push(`(${modelFilter})`);
      }

      // Workspace filter
      if (selectedWorkspaces.length > 0) {
        const workspaceFilter = selectedWorkspaces.map((ws) => `workspace = '${ws}'`).join(' OR ');
        filterParts.push(`(${workspaceFilter})`);
      }

      // User filter
      if (selectedUsers.length > 0) {
        const userFilter = selectedUsers.map((user) => `user = '${user}'`).join(' OR ');
        filterParts.push(`(${userFilter})`);
      }

      // Combine all filter parts with AND
      if (filterParts.length > 0) {
        filterString = filterParts.join(' AND ');
      }

      const response = await TelemetryService.calculateTokenUsage({
        startTime: startTimeISO,
        endTime: endTimeISO,
        granularity,
        groupBy,
        ...(filterString && { filter: filterString }),
      });

      return response.usage || [];
    },
    {
      // Keep data fresh for 30 minutes
      staleTime: 1000 * 60 * 30,
      // Cache for 30 minutes
      cacheTime: 1000 * 60 * 30,
      // Retry failed requests twice
      retry: 2,
      // Retry delay increases exponentially
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Only fetch when component is mounted
      refetchOnWindowFocus: false,
      // Don't refetch on reconnect
      refetchOnReconnect: false,
      // Handle errors
      onError: (error: Error) => {
        console.error('[useTokenUsageData] Error fetching token usage data:', error);
      },
    }
  );

  return {
    usageData: query.data,
    isLoading: query.isLoading,
    error: query.error || undefined,
    refetch: query.refetch,
  };
}
